/**
 * MARKEDSPLASSEN — der byråene tilbyr og arrangørene finner (migrering 0097).
 *
 * ---------------------------------------------------------------------------------------------
 * Atle, 9. september: «Begynn å kod markedsplassen for bookingbyråene.» Byggeplanen fase 3 kaller
 * den «flaten der arrangører og bookingbyråer møtes — tilbud, forespørsler, tilgjengelighet», og
 * PAGES.md er konkret: «Byråportal der bookingbyråer selv legger inn og vedlikeholder artister med
 * asking fee.»
 *
 * ---------------------------------------------------------------------------------------------
 * DETTE ER DEN FØRSTE FLATEN I TVITVI SOM IKKE KREVER EN AVTALE MELLOM TO HUS — og det er hele
 * poenget med den. Alt annet på tvers av hus (0092 speilingen, 0095 rosteren, 0096 giggene) krever
 * en godtatt kobling, fordi partene allerede har funnet hverandre. En markedsplass er stedet man
 * finner noen man IKKE har en avtale med.
 *
 * Det som gjør den trygg er derfor ikke hvem som spør, men hva som svares:
 *
 *   * bare rader byrået SELV har publisert — publisering er en handling, ikke en tilstand,
 *   * bare fra hus av typen `agency`,
 *   * og bare feltene et tilbud består av.
 *
 * INGEN PRIS FRA EN FORHANDLING. PAGES.md: «Prisen én arrangør får deles aldri med andre
 * arrangører.» `askingFeeMinor` er byråets eget tall, satt av dem og publisert av dem. Bud,
 * prishistorikk, score, anslag og notater er ikke med i det hele tatt.
 */
import { z } from 'zod';

export const marketplaceListingDtoSchema = z
  .object({
    /** Byråets artistrad. Den kan ikke åpnes av andre hus — den er en id for «legg til hos meg». */
    artistId: z.uuid(),
    artistName: z.string(),
    genre: z.string().nullable(),
    artistType: z.string().nullable(),
    homeTown: z.string().nullable(),
    /** Antallet som reiser: formatets, ellers artistens. Null når byrået ikke har satt det (0999). */
    partySize: z.number().int().nullable(),
    /** Byråets ASKING FEE. `null` når de ikke har satt en — 0 i basen betyr «ikke satt». */
    askingFeeMinor: z.number().int().nullable(),
    currency: z.string().length(3),
    /** Salgsteksten byrået skrev. Kort med vilje: dette er en annonse, ikke en presentasjon. */
    pitch: z.string().nullable(),
    spotifyUrl: z.string().nullable(),
    imageUrl: z.string().nullable(),
    agencyOrganizationId: z.uuid(),
    agencyName: z.string(),
    publishedAt: z.iso.datetime({ offset: true }),
    /**
     * STÅR ARTISTEN ALT I VÅRT EGET REGISTER? (QA runde 9, P3 #13.)
     *
     * «Ta inn i registeret» så helt lik ut før og etter at man trykket, fordi raden ikke bar dette.
     * Knappen hadde bokstavelig talt ingenting å skifte til — den ga en toast og ble stående, og
     * den som blar videre i lista ser ikke hvilke de alt har tatt inn.
     *
     * Svaret regnes med den SAMME prøven `adopt` gjør, slik at knappen lover det handlingen
     * faktisk kommer til å gjøre.
     */
    inRegister: z.boolean(),
    /**
     * FORMATET SOM ER LAGT UT (sak 215.30). En annonse er én rad per format siden 0120, og
     * `app.marketplace_listings` gir formatet som eget felt siden 0382 — navnet bærer det ikke
     * lenger. Nullbar for sikkerhets skyld; funksjonen leser fra formattabellen, så i praksis satt.
     */
    formatId: z.uuid().nullable(),
    formatName: z.string().nullable(),
  })
  .meta({ id: 'MarketplaceListing' });

/**
 * Ikke paget i første omgang, og taket står i SQL-en (200).
 *
 * En markedsplass med tusen annonser trenger paging og fasetter; en med tjue trenger et søkefelt.
 * Å bygge det første før det andre finnes ville vært å bygge for en trafikk ingen har.
 */
export const marketplaceListingsDtoSchema = z
  .object({ items: z.array(marketplaceListingDtoSchema) })
  .meta({ id: 'MarketplaceListings' });

export const marketplaceQuerySchema = z
  .object({
    search: z.string().trim().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
  })
  .meta({ id: 'MarketplaceQuery' });

/**
 * Å PUBLISERE, eller å ta annonsen ned igjen.
 *
 * `published` er en bryter og ikke et tidspunkt: klienten sier hva den vil, serveren stempler når.
 * En klient som sendte sin egen `publishedAt` ville satt rekkefølgen i lista fra sin egen klokke.
 */
export const publishToMarketplaceRequestSchema = z
  .object({
    published: z.boolean(),
    pitch: z.string().trim().max(600).nullish(),
    /** Byråets asking fee, satt fra det samme kortet — det er tallet annonsen viser. */
    askingFeeMinor: z.number().int().min(0).max(1_000_000_000).nullish(),
  })
  .meta({ id: 'PublishToMarketplaceRequest' });

/**
 * Å TA EN ANNONSE INN I SITT EGET REGISTER.
 *
 * Arrangøren får sin EGEN artistrad — navnet, sjangeren, hjembyen og byråets asking fee — og en
 * byrårad å henge den på. Ingen deling, ingen peker over husgrensen: nøyaktig den samme formen som
 * speilingen (vedtak 94.1) gir den andre veien, og derfor kan den vanlige budkjeden brukes med én
 * gang uten et eneste unntak.
 */
export const adoptListingResponseSchema = z
  .object({
    artistId: z.uuid(),
    artistName: z.string(),
    /** Sant når raden alt fantes — da er dette et oppslag, ikke en opprettelse. */
    alreadyInRegister: z.boolean(),
    /**
     * Sant når raden fantes uten et byrå i Tvitvi og nå er koblet til byrået som la artisten ut:
     * forespørsler på den går dit (260.33).
     */
    linkedToAgency: z.boolean(),
  })
  .meta({ id: 'AdoptListingResponse' });

export type MarketplaceListingDto = z.infer<typeof marketplaceListingDtoSchema>;
export type MarketplaceListingsDto = z.infer<typeof marketplaceListingsDtoSchema>;
export type MarketplaceQuery = z.infer<typeof marketplaceQuerySchema>;
export type PublishToMarketplaceRequest = z.infer<typeof publishToMarketplaceRequestSchema>;
export type AdoptListingResponse = z.infer<typeof adoptListingResponseSchema>;

/**
 * «SEND TIL ARRANGØRER DU JOBBER MED» (§5.7 øverst, vedtak 107.1).
 *
 * Ett send, én eller flere mottakere, ett eller flere formater. Prisen og reisefølget kan rettes
 * for NETTOPP dette sendet — og §5.7 er tydelig på hva det betyr: «The override is one-off per
 * send. It does not stick to that festival and it never changes the artist's standing fee.»
 *
 * Derfor står tallene på hvert FORMAT i sendet, ikke på artisten: man sender «trioen til 90» og
 * «full band til 160» i samme slengen, og de to prisene er ikke den samme overstyringen.
 */
export const agencyOfferFormatSchema = z
  .object({
    formatId: z.uuid(),
    /** Engangsprisen. Står den tom, gjelder formatets stående honorar. */
    amountMinor: z.number().int().min(0).max(1_000_000_000).nullish(),
    /** Reisefølget for dette sendet. Samme regel som prisen. */
    partySize: z.number().int().min(0).max(200).nullish(),
  })
  .meta({ id: 'AgencyOfferFormat' });
export type AgencyOfferFormat = z.infer<typeof agencyOfferFormatSchema>;

export const sendAgencyOfferRequestSchema = z
  .object({
    bookingArtistId: z.uuid(),
    /** Arrangørene som skal få det. Hver får sin EGEN rad, og ser aldri en annens pris. */
    counterpartyOrganizationIds: z.array(z.uuid()).min(1).max(50),
    formats: z.array(agencyOfferFormatSchema).min(1).max(10),
    message: z.string().trim().max(4000).nullish(),
  })
  .meta({ id: 'SendAgencyOfferRequest' });
export type SendAgencyOfferRequest = z.infer<typeof sendAgencyOfferRequestSchema>;

export const sendAgencyOfferResponseSchema = z
  .object({
    /** Hvor mange rader som faktisk ble skrevet — mottakere uten kobling teller ikke. */
    sent: z.number().int(),
    skipped: z.number().int(),
  })
  .meta({ id: 'SendAgencyOfferResponse' });
export type SendAgencyOfferResponse = z.infer<typeof sendAgencyOfferResponseSchema>;
